import { useRef, useState, type ReactNode, type RefObject } from 'react';
import { Input } from '@voll-intelligence/ui-kit/ui/input';
import { TravelCalendar, type TravelCalendarBaseProps } from '../travel_calendar.js';
import { TravelCalendarConfirm } from '../travel_calendar_confirm.js';
import { DateTrigger, Picker, type DateFieldCopy } from './date_field.js';

/**
 * The FORM date-and-time field: the date field's calendar (popover on the wide stage, full-screen
 * sheet below `sm`, a draft that only Confirm commits) with the hour and the minute typed under it.
 * The platform's own `datetime-local` is never used — on Android it opens the system dialog, out of
 * the design system. Values are `YYYY-MM-DDTHH:mm`, with no zone. The field cannot be cleared:
 * Confirm waits for a day, an hour (0–23) and a minute (0–59).
 */

export type DateTimeFieldCopy = Omit<DateFieldCopy, 'format' | 'summary' | 'required'> & {
  readonly hour: string;
  readonly minute: string;
  /** A picked moment on the trigger. */
  readonly format: (value: string) => string;
  /** The draft above Confirm, once it is complete. */
  readonly summary: (value: string) => string;
};

type DateTimeFieldProps = {
  readonly label: string;
  /** `YYYY-MM-DDTHH:mm`; empty = nothing picked. */
  readonly value: string;
  readonly copy: DateTimeFieldCopy;
  readonly locale: string;
  readonly timeline?: TravelCalendarBaseProps['timeline'];
  readonly minDate?: string;
  readonly maxDate?: string;
  readonly disabled?: boolean;
  readonly onChange: (value: string) => void;
};

/** What the calendar and the time of one opening read. */
type DraftProps = Pick<
  DateTimeFieldProps,
  'value' | 'copy' | 'locale' | 'timeline' | 'minDate' | 'maxDate'
>;

export const pad = (digits: string) => digits.padStart(2, '0');
export const within = (digits: string, max: number) =>
  /^\d{1,2}$/.test(digits) && Number(digits) <= max;

function TimeInput({
  label,
  value,
  onChange,
}: {
  readonly label: string;
  readonly value: string;
  readonly onChange: (digits: string) => void;
}): ReactNode {
  return (
    <label className="flex flex-1 flex-col gap-1 text-xs font-medium text-muted-foreground">
      {label}
      <Input
        type="text"
        inputMode="numeric"
        autoComplete="off"
        maxLength={2}
        value={value}
        className="h-10 text-center text-base text-foreground tabular-nums"
        // Typing replaces what is there: a filled "08" would refuse "09" past the 2-digit cap.
        onFocus={(e) => {
          e.currentTarget.select();
        }}
        onChange={(e) => {
          onChange(e.target.value.replace(/\D/g, ''));
        }}
      />
    </label>
  );
}

/** Hour and minute typed side by side, under an optional caption ("Ida", "Volta"). */
export function TimeRow({
  copy,
  caption,
  hour,
  minute,
  onHour,
  onMinute,
}: {
  readonly copy: { readonly hour: string; readonly minute: string };
  readonly caption?: string;
  readonly hour: string;
  readonly minute: string;
  readonly onHour: (digits: string) => void;
  readonly onMinute: (digits: string) => void;
}): ReactNode {
  const row = (
    <div className="flex w-full items-end gap-2">
      <TimeInput label={copy.hour} value={hour} onChange={onHour} />
      <span aria-hidden className="pb-2 text-base font-medium">
        :
      </span>
      <TimeInput label={copy.minute} value={minute} onChange={onMinute} />
    </div>
  );
  if (!caption) return row;
  return (
    <fieldset className="m-0 grid w-full gap-1 border-0 p-0">
      <legend className="text-sm font-medium text-foreground">{caption}</legend>
      {row}
    </fieldset>
  );
}

/** The calendar only blocks NEW picks outside the window; a day that came in with the value can sit past it. */
const inWindow = (day: string, minDate?: string, maxDate?: string) =>
  (!minDate || day >= minDate) && (!maxDate || day <= maxDate);

/** The calendar of one opening, the time under it and a Confirm that lights up once all is set. */
function DateTimeDraft({
  props,
  sheet,
  onConfirm,
}: {
  readonly props: DraftProps;
  readonly sheet: boolean;
  readonly onConfirm: (value: string) => void;
}): ReactNode {
  const { copy, locale, timeline, minDate, maxDate } = props;
  const [initialDay = '', initialTime = ''] = props.value.split('T');
  const [initialHour = '', initialMinute = ''] = initialTime.split(':');
  const [day, setDay] = useState(initialDay);
  const [hour, setHour] = useState(initialHour);
  const [minute, setMinute] = useState(initialMinute);
  const complete =
    Boolean(day) && inWindow(day, minDate, maxDate) && within(hour, 23) && within(minute, 59);
  const moment = `${day}T${pad(hour)}:${pad(minute)}`;
  const footer = (
    <>
      <TimeRow copy={copy} hour={hour} minute={minute} onHour={setHour} onMinute={setMinute} />
      <TravelCalendarConfirm
        summary={complete ? copy.summary(moment) : ''}
        confirmLabel={copy.confirm}
        disabled={!complete}
        full={sheet}
        onConfirm={() => {
          if (complete) onConfirm(moment);
        }}
      />
    </>
  );
  return (
    <TravelCalendar
      mode="single"
      layout={sheet ? 'sheet' : 'popover'}
      months={1}
      value={day ? { from: day } : {}}
      locale={locale}
      previousMonthLabel={copy.previousMonth}
      nextMonthLabel={copy.nextMonth}
      {...(timeline ? { timeline } : {})}
      {...(minDate ? { minDate } : {})}
      {...(maxDate ? { maxDate } : {})}
      onChange={(next) => {
        setDay(next.from ?? '');
      }}
      footer={footer}
    />
  );
}

export type DateTimePickerProps = DraftProps & {
  readonly open: boolean;
  readonly onClose: () => void;
  /** What the popover hangs from, and where focus returns on Confirm. */
  readonly triggerRef: RefObject<HTMLElement | null>;
  readonly onConfirm: (value: string) => void;
};

/** The field's calendar and time without its trigger, for a screen that keeps its own. */
export function DateTimePicker(props: DateTimePickerProps): ReactNode {
  const { open, onClose, triggerRef, copy, onConfirm } = props;
  return (
    <Picker
      open={open}
      title={copy.title}
      back={copy.back}
      triggerRef={triggerRef}
      onClose={onClose}
    >
      {(sheet) => (
        <DateTimeDraft
          props={props}
          sheet={sheet}
          onConfirm={(next) => {
            onClose();
            triggerRef.current?.focus();
            onConfirm(next);
          }}
        />
      )}
    </Picker>
  );
}

/** A day and a time of day: where the KM trip left and arrived. */
export function DateTimeField(props: DateTimeFieldProps): ReactNode {
  const { label, value, copy, disabled, onChange } = props;
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  return (
    <>
      <DateTrigger
        label={label}
        text={value ? copy.format(value) : copy.placeholder}
        open={open}
        disabled={disabled}
        triggerRef={triggerRef}
        extras={{}}
        onOpen={() => {
          setOpen(true);
        }}
      />
      <DateTimePicker
        {...props}
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        triggerRef={triggerRef}
        onConfirm={onChange}
      />
    </>
  );
}
